import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CommentDeleteDialog } from "@/components/ui/comment-delete-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import ErrorComponent from "@/components/ui/error";
import LoadingScreen from "@/components/ui/loading-screen";
import { Select } from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  INSPECTION_PORTAL_IMPERSONATE_URL_PARAM,
  INSPECTION_PORTAL_IMPERSONATE_USER_URL_PARAM,
  UserType,
} from "@/constants";
import {
  getLicenseCommentsOptions,
  getLicensePaymentsOptions,
  getLicenseTypeQuestionsOptions,
  getNonRegistrantLicenseCommentsOptions,
  getNonRegistrantLicensePaymentsOptions,
} from "@/hera-api/client/@tanstack/react-query.gen";
import { updateInspection } from "@/hera-api/client/sdk.gen";
import type {
  InspectionStatus,
  InspectionWithLicenseResponseSchema,
} from "@/hera-api/client/types.gen";
import { useAuthQuerySetup } from "@/hooks/use-auth-query-setup";
import { useCommentHandlers } from "@/hooks/use-comment-handlers";
import { useLicenseDetailsWithRuntimeRefresh } from "@/hooks/use-license-details-with-runtime-refresh";
import {
  type InspectionImpersonationCandidate,
  listInspectionImpersonationCandidates,
} from "@/lib/inspection-portal-api";
import { refreshLicensesAfterLocalChange } from "@/lib/license-details-change-notification";
import {
  getInspectionStatusDisplay,
  getQueryErrorMessage,
  isInspectionStatusEditable,
} from "@/lib/utils";
import { useAuth0 } from "@auth0/auth0-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
  Edit,
  ExternalLink,
  ThumbsDown,
  ThumbsUp,
  XCircle,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CommentsCardContent } from "../license-details/cards/comments-card";
import { InspectionAnswersPanel } from "../license-details/cards/inspection-answers-panel";
import { InspectionDisplay } from "../license-details/cards/inspections-card";
import { LicenseInfoCardContent } from "../license-details/cards/license-info-card";
import {
  RegistrationCurrentPeriodSummaryCard,
  useLicensePaymentsDerived,
} from "../license-details/cards/license-payments-derived";
import { PaymentsCardContent } from "../license-details/cards/payments-card";
import { PropertyCardContent } from "../license-details/cards/property-card";
import {
  RegistrationDetailsInlineSpinner,
  RegistrationDetailsPaymentsLoading,
} from "../license-details/registration-details-section-loading";
import { PerformInspectionFlow } from "./perform-inspection-flow";

interface InspectionDetailsSheetProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  inspection: InspectionWithLicenseResponseSchema | null;
  userType: UserType;
  onInspectionUpdated?: () => void;
  elevated?: boolean;
}

export function InspectionDetailsSheet({
  isOpen,
  onOpenChange,
  inspection,
  userType,
  onInspectionUpdated,
  elevated = false,
}: InspectionDetailsSheetProps) {
  const { isAuthSetupComplete } = useAuthQuerySetup();
  const { getAccessTokenSilently } = useAuth0();
  const [isPerformingInspection, setIsPerformingInspection] = useState(false);
  const [forceInspectionEdit, setForceInspectionEdit] = useState(false);
  const [candidateDialogOpen, setCandidateDialogOpen] = useState(false);
  const [candidates, setCandidates] = useState<
    InspectionImpersonationCandidate[]
  >([]);
  const [selectedCandidateId, setSelectedCandidateId] = useState("");
  const [openingPortal, setOpeningPortal] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Reset edit mode when sheet closes or opens
  useEffect(() => {
    if (!isOpen) {
      setForceInspectionEdit(false);
    }
  }, [isOpen]);

  const {
    licenseData,
    isPreviewLoading: isLicensePreviewLoading,
    isRuntimeRefreshing: isLicenseRuntimeRefreshing,
    previewError: licenseError,
  } = useLicenseDetailsWithRuntimeRefresh({
    licenseId: inspection?.license_id ?? null,
    enabled: isAuthSetupComplete && !!inspection?.license_id,
  });

  const licenseTypeId = licenseData?.license_type?.id;
  const jurisdictionId = licenseData?.jurisdiction?.id;

  const { data: questionsData } = useQuery({
    ...getLicenseTypeQuestionsOptions({
      path: {
        license_type_id: licenseTypeId ?? "",
        jurisdiction_id: jurisdictionId ?? "",
      },
      query: { include_deleted: false },
    }),
    enabled:
      isAuthSetupComplete &&
      !!licenseData &&
      !!licenseTypeId &&
      !!jurisdictionId,
  });

  const inspectionQuestions = useMemo(
    () =>
      (questionsData?.questions ?? []).filter(
        (q) => q.question_type === "inspection",
      ),
    [questionsData?.questions],
  );

  // Comments query for non-registrant users
  const {
    data: nonRegistrantCommentsData,
    isLoading: isNonRegistrantCommentsLoading,
  } = useQuery({
    ...getNonRegistrantLicenseCommentsOptions({
      path: {
        license_id: inspection?.license_id ?? "",
      },
    }),
    enabled:
      userType !== UserType.REGISTRANT &&
      isAuthSetupComplete &&
      !!inspection?.license_id,
  });

  // Comments query for registrant
  const {
    data: registrantCommentsData,
    isLoading: isRegistrantCommentsLoading,
  } = useQuery({
    ...getLicenseCommentsOptions({
      path: {
        license_id: inspection?.license_id ?? "",
      },
    }),
    enabled:
      userType === UserType.REGISTRANT &&
      isAuthSetupComplete &&
      !!inspection?.license_id,
  });

  // Payments query for non-registrant users
  const {
    data: nonRegistrantPaymentsData,
    isLoading: isNonRegistrantPaymentsLoading,
  } = useQuery({
    ...getNonRegistrantLicensePaymentsOptions({
      path: {
        license_id: inspection?.license_id ?? "",
      },
    }),
    enabled:
      userType !== UserType.REGISTRANT &&
      isAuthSetupComplete &&
      !!inspection?.license_id,
  });

  // Payments query for registrant
  const {
    data: registrantPaymentsData,
    isLoading: isRegistrantPaymentsLoading,
  } = useQuery({
    ...getLicensePaymentsOptions({
      path: {
        license_id: inspection?.license_id ?? "",
      },
    }),
    enabled:
      userType === UserType.REGISTRANT &&
      isAuthSetupComplete &&
      !!inspection?.license_id,
  });

  // Select the correct comments data and loading/error states
  const commentsData =
    userType === UserType.REGISTRANT
      ? registrantCommentsData
      : nonRegistrantCommentsData;
  const isCommentsLoading =
    userType === UserType.REGISTRANT
      ? isRegistrantCommentsLoading
      : isNonRegistrantCommentsLoading;

  // Select the correct payments data
  const paymentsData =
    userType === UserType.REGISTRANT
      ? registrantPaymentsData
      : nonRegistrantPaymentsData;
  const isPaymentsLoading =
    userType === UserType.REGISTRANT
      ? isRegistrantPaymentsLoading
      : isNonRegistrantPaymentsLoading;

  const paymentsDerived = useLicensePaymentsDerived({
    payments: paymentsData ?? { payments: [] },
    userType,
    licenseId: inspection?.license_id ?? "",
    licenseData: licenseData,
  });

  // Comment handlers
  const commentHandlers = useCommentHandlers({
    licenseId: inspection?.license_id ?? "",
  });

  // Mutation to cancel inspection
  const cancelInspectionMutation = useMutation({
    mutationFn: async () => {
      if (!inspection) return;
      return await updateInspection({
        path: {
          inspection_id: inspection.id,
        },
        body: {
          status: "CANCELLED" as InspectionStatus,
        },
      });
    },
    onSuccess: () => {
      toast.success("Inspection cancelled");
      queryClient.invalidateQueries({
        queryKey: ["inspections"],
      });
      void refreshLicensesAfterLocalChange(
        queryClient,
        inspection?.license_id ?? [],
      );
      onInspectionUpdated?.();
    },
    onError: (error) => {
      toast.error(getQueryErrorMessage(error));
    },
  });

  const reviewInspectionMutation = useMutation({
    mutationFn: async (nextStatus: "COMPLIANT" | "INSPECTION_PENDING") => {
      if (!inspection) return;
      return await updateInspection({
        path: { inspection_id: inspection.id },
        body: { status: nextStatus as InspectionStatus },
      });
    },
    onSuccess: (_data, nextStatus) => {
      toast.success(
        nextStatus === "COMPLIANT"
          ? "Inspection approved"
          : "Inspection rejected — returned to pending",
      );
      queryClient.invalidateQueries({ queryKey: ["inspections"] });
      queryClient.invalidateQueries({ queryKey: ["licenseDetails"] });
      void refreshLicensesAfterLocalChange(
        queryClient,
        inspection?.license_id ?? [],
      );
      onInspectionUpdated?.();
    },
    onError: (error) => {
      toast.error(getQueryErrorMessage(error));
    },
  });

  if (!inspection) {
    return null;
  }

  const handlePerformInspection = () => {
    setIsPerformingInspection(true);
  };

  const handleInspectionCompleted = () => {
    setIsPerformingInspection(false);
    onInspectionUpdated?.();
    onOpenChange(false);
  };

  const handleCancelPerformInspection = () => {
    setIsPerformingInspection(false);
  };

  const handleCancelInspection = () => {
    if (
      confirm(
        "Are you sure you want to cancel this inspection? This action cannot be undone.",
      )
    ) {
      cancelInspectionMutation.mutate();
    }
  };

  const buildInspectionPortalUrl = (
    accountId: string,
    userId?: string | null,
  ) => {
    const params = new URLSearchParams({
      [INSPECTION_PORTAL_IMPERSONATE_URL_PARAM]: accountId,
    });
    if (userId?.trim()) {
      params.set(INSPECTION_PORTAL_IMPERSONATE_USER_URL_PARAM, userId.trim());
    }
    return `/inspection/queue?${params.toString()}`;
  };

  const openInspectionPortal = async () => {
    if (!licenseData) return;
    const accountId = licenseData.registrant_account?.id ?? null;
    if (!accountId) {
      toast.error("No registrant account found for this license");
      return;
    }

    setOpeningPortal(true);
    try {
      const assigneeId = inspection.assignee_id;
      if (assigneeId) {
        window.open(buildInspectionPortalUrl(accountId, assigneeId), "_blank");
        return;
      }

      const results = await listInspectionImpersonationCandidates(
        () => getAccessTokenSilently(),
        licenseData.id,
      );
      if (results.length === 1) {
        window.open(
          buildInspectionPortalUrl(
            accountId,
            results[0]?.login_identity_id ?? null,
          ),
          "_blank",
        );
        return;
      }
      if (results.length > 1) {
        setCandidates(results);
        setSelectedCandidateId(results[0]?.login_identity_id ?? "");
        setCandidateDialogOpen(true);
        return;
      }

      window.open(buildInspectionPortalUrl(accountId), "_blank");
    } catch (error) {
      toast.error(getQueryErrorMessage(error));
    } finally {
      setOpeningPortal(false);
    }
  };

  if (isPerformingInspection) {
    return (
      <PerformInspectionFlow
        inspection={inspection}
        license={licenseData ?? null}
        userType={userType}
        onComplete={handleInspectionCompleted}
        onCancel={handleCancelPerformInspection}
      />
    );
  }

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        elevated={elevated}
        className="w-full sm:max-w-2xl overflow-y-auto px-6"
      >
        <SheetHeader>
          <SheetTitle>Inspection Details</SheetTitle>
          <SheetDescription className="hidden">
            View inspection details
          </SheetDescription>
        </SheetHeader>

        {isLicensePreviewLoading && !licenseData ? (
          <div className="flex justify-center items-center py-8">
            <LoadingScreen message="Loading inspection details..." />
          </div>
        ) : licenseError ? (
          <ErrorComponent
            title="Error loading inspection"
            message="Failed to load inspection details"
          />
        ) : licenseData ? (
          <div className="w-full space-y-6 mt-6 @container">
            {/* Inspection Details Tile - at the top */}
            <Card className="gap-4">
              <CardHeader className="flex flex-row items-start justify-between">
                <CardTitle className="font-semibold text-xl">
                  Inspection
                </CardTitle>
                {(userType === UserType.OPERATOR ||
                  userType === UserType.GOVERNMENT) &&
                  (() => {
                    const display = getInspectionStatusDisplay(
                      inspection.status,
                      inspection.has_awaiting_check_payment,
                      licenseData.status !== "PAID_PENDING_REVIEW",
                      inspection.has_awaiting_invoice_payment,
                    );
                    return (
                      isInspectionStatusEditable(inspection.status) &&
                      display.label !== "Awaiting Registration Approval"
                    );
                  })() && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="min-w-[80px]"
                      onClick={() => setForceInspectionEdit((prev) => !prev)}
                    >
                      <Edit className="h-4 w-4 mr-2" /> Edit
                    </Button>
                  )}
              </CardHeader>
              <CardContent>
                <InspectionDisplay
                  inspection={{
                    id: inspection.id,
                    status: inspection.status,
                    effective_date: inspection.effective_date,
                    effective_end_date: inspection.effective_end_date,
                    cancellation_date: inspection.cancellation_date,
                    cancellation_reason: inspection.cancellation_reason,
                    created_at: inspection.created_at,
                    assignee_id: inspection.assignee_id,
                    answers_to_additional_questions:
                      inspection.answers_to_additional_questions,
                  }}
                  hasAwaitingCheckPaymentOverride={
                    inspection.has_awaiting_check_payment
                  }
                  hasAwaitingInvoicePaymentOverride={
                    inspection.has_awaiting_invoice_payment
                  }
                  userType={userType}
                  onInspectorUpdated={onInspectionUpdated}
                  inspectionJurisdictionId={licenseData.jurisdiction?.id}
                  licenseStatus={licenseData?.status}
                  forceEdit={forceInspectionEdit}
                  onEditModeChange={(isEditing) => {
                    setForceInspectionEdit(isEditing);
                  }}
                  showStatusInfoButton
                />
              </CardContent>
            </Card>

            {(userType === UserType.OPERATOR ||
              userType === UserType.GOVERNMENT) &&
            (inspection.answers_to_additional_questions ||
              inspectionQuestions.length > 0) ? (
              <Card className="gap-4">
                <CardHeader>
                  <CardTitle className="font-semibold text-xl">
                    Inspection Answers
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <InspectionAnswersPanel
                    questions={inspectionQuestions}
                    answers={inspection.answers_to_additional_questions}
                    elevatedDialog
                    property={licenseData.property}
                  />
                </CardContent>
              </Card>
            ) : null}

            {/* Actions Tile */}
            <Card className="gap-4">
              <CardHeader>
                <CardTitle className="font-semibold text-xl">Actions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {userType === UserType.OPERATOR ? (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full min-w-[80px]"
                      disabled={openingPortal}
                      onClick={() => void openInspectionPortal()}
                    >
                      <ExternalLink className="h-4 w-4" />
                      <span className="ml-1">Open in Inspection Portal</span>
                    </Button>
                  ) : null}
                  {inspection.status === "INSPECTION_PENDING" && (
                    <Button
                      variant="default"
                      size="sm"
                      className="w-full min-w-[80px]"
                      onClick={handlePerformInspection}
                    >
                      Perform Inspection
                    </Button>
                  )}
                  {(userType === UserType.OPERATOR ||
                    userType === UserType.GOVERNMENT) &&
                    inspection.status === "COMMITTED" && (
                      <>
                        <Button
                          variant="default"
                          size="sm"
                          className="w-full min-w-[80px]"
                          disabled={reviewInspectionMutation.isPending}
                          onClick={() =>
                            reviewInspectionMutation.mutate("COMPLIANT")
                          }
                        >
                          <ThumbsUp className="h-4 w-4" />
                          <span className="ml-1">Approve Inspection</span>
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full min-w-[80px]"
                          disabled={reviewInspectionMutation.isPending}
                          onClick={() => {
                            if (
                              confirm(
                                "Reject this inspection and return it to Inspection Pending?",
                              )
                            ) {
                              reviewInspectionMutation.mutate(
                                "INSPECTION_PENDING",
                              );
                            }
                          }}
                        >
                          <ThumbsDown className="h-4 w-4" />
                          <span className="ml-1">Reject Inspection</span>
                        </Button>
                      </>
                    )}
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full min-w-[80px] text-destructive hover:bg-destructive hover:text-destructive-foreground"
                    onClick={handleCancelInspection}
                    disabled={cancelInspectionMutation.isPending}
                  >
                    <XCircle className="h-4 w-4" />
                    <span className="ml-1">Cancel</span>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Property Tile */}
            {licenseData.property && (
              <Card className="gap-4">
                <CardHeader className="flex flex-row items-start justify-between">
                  <CardTitle className="font-semibold text-xl">
                    Property
                  </CardTitle>
                </CardHeader>
                <PropertyCardContent
                  property={licenseData.property}
                  jurisdiction={licenseData.jurisdiction}
                  userType={userType}
                  licenseId={licenseData.id}
                />
              </Card>
            )}

            {/* License Tile */}
            <Card
              className="gap-4 cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={() => {
                if (userType === UserType.OPERATOR) {
                  // For operator users, navigate to operator dashboard with license ID in URL
                  navigate({
                    to: "/operator/registrations",
                    search: { licenseId: licenseData.id },
                  });
                  onOpenChange(false);
                } else if (userType === UserType.GOVERNMENT) {
                  // For government users, navigate to government dashboard with license ID in URL
                  navigate({
                    to: "/government/registrations",
                    search: { licenseId: licenseData.id },
                  });
                  onOpenChange(false);
                } else {
                  // For registrant users, use the registrant route
                  navigate({
                    to: "/registrant/licenses/$licenseId/view",
                    params: { licenseId: licenseData.id },
                  });
                  onOpenChange(false);
                }
              }}
            >
              <CardHeader className="flex flex-row items-start justify-between">
                <CardTitle className="font-semibold text-xl">License</CardTitle>
              </CardHeader>
              <LicenseInfoCardContent
                data={licenseData}
                userType={userType}
                licenseId={licenseData.id}
                showRegistrationCertificateButton={!licenseData.property}
              />
            </Card>

            {/* Comments Tile */}
            <Card className="gap-4">
              <CardHeader>
                <CardTitle className="font-semibold text-xl">
                  Comments
                </CardTitle>
              </CardHeader>
              {isCommentsLoading ? (
                <RegistrationDetailsInlineSpinner label="Loading comments" />
              ) : commentsData ? (
                <CommentsCardContent
                  commentsData={commentsData}
                  userType={userType}
                  onCommentAdded={
                    inspection?.license_id
                      ? commentHandlers.handleAddComment
                      : undefined
                  }
                  onCommentDeleted={
                    inspection?.license_id
                      ? commentHandlers.handleDeleteComment
                      : undefined
                  }
                />
              ) : null}
            </Card>

            {/* Current period summary + Payments */}
            {isPaymentsLoading ? (
              <RegistrationDetailsPaymentsLoading userType={userType} />
            ) : paymentsData ? (
              <>
                <RegistrationCurrentPeriodSummaryCard
                  derived={paymentsDerived}
                  isRefreshing={isLicenseRuntimeRefreshing}
                  userType={userType}
                />
                <Card className="gap-4">
                  <CardHeader>
                    <CardTitle className="font-semibold text-xl">
                      Payments
                    </CardTitle>
                  </CardHeader>
                  <PaymentsCardContent
                    payments={paymentsData}
                    userType={userType}
                    licenseId={inspection?.license_id ?? ""}
                    licenseData={licenseData}
                    derived={paymentsDerived}
                  />
                </Card>
              </>
            ) : null}
          </div>
        ) : null}

        {/* Comment delete confirmation dialog */}
        <CommentDeleteDialog
          isOpen={commentHandlers.isDeleteDialogOpen}
          onOpenChange={commentHandlers.setIsDeleteDialogOpen}
          onConfirm={commentHandlers.handleConfirmDelete}
        />
        <Dialog
          open={candidateDialogOpen}
          onOpenChange={setCandidateDialogOpen}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Open inspection portal as</DialogTitle>
              <DialogDescription>
                Select which user to open the inspection portal as for this
                license.
              </DialogDescription>
            </DialogHeader>
            <Select
              options={candidates.map((candidate) => ({
                value: candidate.login_identity_id,
                label: candidate.name
                  ? `${candidate.email} (${candidate.name})`
                  : candidate.email,
              }))}
              value={selectedCandidateId ? [selectedCandidateId] : []}
              onValueChange={(values) =>
                setSelectedCandidateId(values[0] || "")
              }
              placeholder="Select user"
            />
            <Button
              type="button"
              disabled={!selectedCandidateId}
              onClick={() => {
                const accountId = licenseData?.registrant_account?.id;
                if (!accountId) return;
                window.open(
                  buildInspectionPortalUrl(accountId, selectedCandidateId),
                  "_blank",
                );
                setCandidateDialogOpen(false);
              }}
            >
              Open portal
            </Button>
          </DialogContent>
        </Dialog>
      </SheetContent>
    </Sheet>
  );
}
